import { SPACE_ELEMENTS_OF_MODAL, STACK_ERROR_SX } from "@components/common/dialog/dialog-constants";
import { GroupAutoComplete } from "@components/common/group-autocomplete/group-autocomplete";
import { LabelWithHelp } from "@components/common/label-with-help";
import { useGroupMembersContext } from "@context-providers/group-members-provider";
import { Dialog, Text, Tooltip } from "@flat-ui";
import { useResolveProjectManagerIdentity } from "@hooks/projects/use-resolve-project-manager-identity";
import { Stack } from "@mui-wrapper";
import { getGroupManagerKey, getGroupMembershipChange, resolveTypedEmail, GROUP_VIEWER_PROMOTION_ROLE } from "@pages/project-details/project-overview/change-management/change-management-utils";
import { useManagerSeat } from "@pages/project-details/project-overview/change-management/manager-seat-section";
import { ManagerSelection } from "@pages/project-details/project-overview/change-management/manager-selection";
import { useManagerCandidates } from "@pages/project-details/project-overview/change-management/use-manager-candidates";
import { usePrepareManager } from "@pages/project-details/project-overview/change-management/use-prepare-manager";
import { groupsSelector } from "@store/groups/groups-selector";
import { areCompanyMembersLoadingSelector } from "@store/members/members-selector";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useEffect, useRef, useState } from "react";

/** The role in the group offered first to a new project manager who changes it */
const DEFAULT_GROUP_ROLE = "PROJECT_MANAGER";

/** What the dialog shows about the new project manager while the change is saved */

/** Dialog to change the project manager and group of the current project */
export function ChangeManagementDialogContent({
  isOpen,
  onClose
}) {
  const dispatch = useAppDispatch();
  const {
    canAssignManagers,
    resolveProjectManagerIdentity
  } = useResolveProjectManagerIdentity();
  const {
    project,
    groupMembers,
    setSelectedGroupId,
    selectedGroupId,
    areGroupMembersLoading,
    refreshGroupMembers
  } = useGroupMembersContext();
  const {
    isWorkspaceAdminView,
    candidates,
    memberNotes
  } = useManagerCandidates(groupMembers, isOpen);
  const groups = useAppSelector(groupsSelector);
  const areWorkspaceMembersLoading = useAppSelector(areCompanyMembersLoadingSelector);

  // Hide the confirm action only when the sole group is already the project's group (nothing to
  // change); an ungrouped project must still be assignable to that lone group.
  const hasNoAlternativeGroup = !canAssignManagers && groups.length === 1 && project?.group?.id === groups[0].id;
  const [chosenProjectManager, setChosenProjectManager] = useState(null);
  // An email typed before the workspace members loaded can be somebody who is a member already
  const selectedProjectManager = resolveTypedEmail(chosenProjectManager, candidates);
  const isSubmittingRef = useRef(false);
  const [isChangingManagement, setIsChangingManagement] = useState(false);
  // Whether the user chose the manager, so that refreshed group members do not prefill it again
  const [hasChosenManager, setHasChosenManager] = useState(false);
  const [groupRole, setGroupRole] = useState(DEFAULT_GROUP_ROLE);
  const [isSeatChecked, setIsSeatChecked] = useState(true);
  // The changes when saving started, since they change while saving, e.g. once the seat is given
  const [savingChanges, setSavingChanges] = useState();

  // The members who manage projects in a group since this dialog added them, as `groupId:identity`
  const [addedGroupManagers, setAddedGroupManagers] = useState(new Set());
  const {
    prepareManager
  } = usePrepareManager({
    onManagerUpdated: selectManager,
    onGroupManagerAdded: (identity, groupId) => setAddedGroupManagers(keys => new Set([...keys, getGroupManagerKey(groupId, identity)]))
  });
  // Only an admin of the workspace choosing the manager can choose somebody who needs to be
  // prepared; anybody else chooses a manager of the group, or is the manager implicitly
  const canChooseAnyManager = canAssignManagers && isWorkspaceAdminView;
  const liveSeat = useManagerSeat(selectedProjectManager);
  const liveChanges = {
    // Until the group members load, everybody would look like they are not in the group
    groupMembershipChange: canChooseAnyManager && !areGroupMembersLoading && !isAddedGroupManager() ? getGroupMembershipChange(selectedProjectManager, groupMembers) : "none",
    seat: {
      ...liveSeat,
      isSeatNeeded: canChooseAnyManager && liveSeat.isSeatNeeded,
      isCheckingSeat: canChooseAnyManager && liveSeat.isCheckingSeat
    }
  };
  const {
    groupMembershipChange,
    seat
  } = isChangingManagement && savingChanges || liveChanges;
  const isSeatMissing = seat.isCheckingSeat || seat.isSeatNeeded && (!isSeatChecked || !!seat.option.blockReason);
  // Only the workspace members tell whether a typed email belongs to a member already, and they
  // are only fetched for the workspace admin view
  const isResolvingManager = canChooseAnyManager && areWorkspaceMembersLoading;

  // Reset the group selection to the project's current group whenever the dialog opens, so a
  // previously changed-then-cancelled selection does not persist across opens. Projects without a
  // group must reset to undefined so a stale selection is not submitted unintentionally.
  useEffect(() => {
    if (isOpen) {
      setSelectedGroupId(project?.group?.id);
      setHasChosenManager(false);
      setGroupRole(DEFAULT_GROUP_ROLE);
      setIsSeatChecked(true);
      setAddedGroupManagers(new Set());
    }
  }, [isOpen, project?.group, setSelectedGroupId]);

  // Prefill the project manager with the current project manager, if the current group of the project is selected
  // Empty it for other groups
  // It waits for the group members, since the current manager would look like an outsider before
  useEffect(() => {
    if (project && isOpen && project.group && !hasChosenManager && !areGroupMembersLoading) {
      if (project.group.id !== selectedGroupId) {
        setChosenProjectManager(null);
        return;
      }
      const managerIdentity = project.managers?.projectManager?.identity;
      const projectManager = groupMembers.find(member_0 => member_0.identity === managerIdentity) ?? candidates.find(member => member.identity === managerIdentity) ?? null;
      setChosenProjectManager(projectManager);
    }
  }, [isOpen, groupMembers, areGroupMembersLoading, candidates, project, selectedGroupId, hasChosenManager]);
  useEffect(() => {
    if (isOpen) {
      isSubmittingRef.current = false;
    }
  }, [isOpen]);

  /**
   * Sends a request to the backend to change the project manager and group of the project
   */
  async function changeProjectManagerAndGroup() {
    // Synchronous guard against very fast double-clicks before state rerender.
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;

    // When the workspace can assign managers the manager is chosen in the dialog. Otherwise the
    // manager selection is hidden and the current user (main admin) is used implicitly.
    const resolvedIdentity = resolveProjectManagerIdentity(selectedProjectManager);

    // Theoretically the user should not be able to reach this state
    // if the manager was not resolved but do this for type safety
    // and as an edge case if our members selection didn't work properly.
    if (!resolvedIdentity) {
      throw new Error("Project manager is required!");
    }
    setSavingChanges(liveChanges);
    setIsChangingManagement(true);
    try {
      const managerIdentity_0 = await getPreparedManagerIdentity(resolvedIdentity);
      if (!managerIdentity_0) {
        return;
      }
      const payload = {
        projectManagerIdentity: managerIdentity_0,
        groupId: String(selectedGroupId)
      };
      await dispatch(updateProjectDetails({
        projectId: project?.id,
        payload
      })).unwrap();
      onClose();
    } catch {
      // The errors slice tells the user why, and the dialog stays open to try again
    } finally {
      // Lets the user try again when a step failed and the dialog stays open
      isSubmittingRef.current = false;
      setIsChangingManagement(false);
    }
  }

  /**
   * Returns the identity of the new project manager once they can manage the project, see
   * prepareManager, or undefined when that failed
   */
  async function getPreparedManagerIdentity(resolvedIdentity_0) {
    if (!canChooseAnyManager || !selectedProjectManager || !selectedGroupId) {
      return resolvedIdentity_0;
    }
    const managerIdentity_1 = await prepareManager({
      selectedMember: selectedProjectManager,
      groupId: selectedGroupId,
      groupMembershipChange,
      groupRole: groupMembershipChange === "changeRole" ? GROUP_VIEWER_PROMOTION_ROLE : groupRole,
      shouldAssignSeat: seat.isSeatNeeded
    });
    if (groupMembershipChange !== "none") {
      refreshGroupMembers();
    }
    return managerIdentity_1;
  }

  /** Whether this dialog already made the chosen manager manage projects in the selected group */
  function isAddedGroupManager() {
    return !!selectedGroupId && typeof selectedProjectManager === "object" && !!selectedProjectManager && addedGroupManagers.has(getGroupManagerKey(selectedGroupId, selectedProjectManager.identity));
  }

  /** Chooses the new project manager, which stops the current one from being prefilled again */
  function selectManager(member_1) {
    setHasChosenManager(true);
    setChosenProjectManager(member_1);
  }

  /** Selects another group, whose project manager has to be chosen again */
  function selectGroup(groupId_0) {
    setHasChosenManager(false);
    setChosenProjectManager(null);
    setSelectedGroupId(groupId_0);
  }
  return <Dialog title={canAssignManagers ? "Change Management" : "Change Group"} confirmText="Change" open={isOpen} onConfirm={hasNoAlternativeGroup ? undefined : changeProjectManagerAndGroup} isConfirmDisabled={(canAssignManagers ? !selectedProjectManager || isSeatMissing || isResolvingManager || areGroupMembersLoading : !selectedGroupId) || isChangingManagement} onCancel={isChangingManagement ? undefined : onClose} showSpinner={isChangingManagement} disabled={isChangingManagement}>
      {canAssignManagers && <Text variant="bodyM" sx={{
      mb: SPACE_ELEMENTS_OF_MODAL
    }}>
          Please select the group and manager to assign this project to.
        </Text>}
      <Stack sx={STACK_ERROR_SX}>
        <LabelWithHelp title="Group" />
        <Tooltip component="div" title={hasNoAlternativeGroup ? "There is no other group available to move this project to." : ""} tooltipSx={{
        maxWidth: "min(24rem, calc(100vw - 2rem))"
      }}>
          <GroupAutoComplete callbackOnGroupSelected={selectGroup} selectedGroupId={selectedGroupId}
        // A group manager only matters when the manager is derived from the group; the
        // Change Group flow uses the current admin implicitly, so allow every group there.
        shouldOnlyShowGroupsWithGroupManager={canAssignManagers} />
        </Tooltip>
      </Stack>
      {canAssignManagers && <ManagerSelection isWorkspaceAdminView={isWorkspaceAdminView} candidates={candidates} memberNotes={memberNotes} selectedMember={selectedProjectManager} onSelectedMemberChange={selectManager} isDisabled={!selectedGroupId} groupName={groups.find(({
      id
    }) => id === selectedGroupId)?.name} groupMembershipChange={groupMembershipChange} groupRole={groupRole} onGroupRoleChange={setGroupRole} seat={seat} isSeatChecked={isSeatChecked} onSeatCheckedChange={setIsSeatChecked} />}
    </Dialog>;
}